import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, Button, Typography, Paper, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { autoJsStoreUtils, autoWebViewJs } from '@droid/android';
import { PermissionStatusPanel } from '../permission-status';
import { authService } from '@src/services/authService';

export const SensorMonitor: React.FC = () => {
  const [threshold, setThreshold] = useState<string>('5.0');
  const [isMonitoring, setIsMonitoring] = useState<boolean>(false);

  // 为每个对话框创建独立的状态
  const [isSensorInfoDialogOpen, setSensorInfoDialogOpen] = useState(false);
  const [isForegroundServiceDialogOpen, setForegroundServiceDialogOpen] = useState(false);

  // 使用useRef来保存回调函数，避免在re-render中丢失
  const sensorCallbackRef = useRef<(passed: boolean) => void>();
  const foregroundServiceCallbackRef = useRef<(passed: boolean) => void>();


  useEffect(() => {
    try {
      const script = `
      var thresholdValue = com.fanfanlo.emergencycall.manager.SensorManager.getThreshold();
      thresholdValue;`;
      const { javaResultString } = autoWebViewJs.callScript(script);
      const thresholdValue = parseFloat(javaResultString || '5.0');
      // console.log("script get threshold=", thresholdValue, javaResultString);
      setThreshold(thresholdValue.toString());
    } catch (error) {
      console.error('Error fetching initial threshold value:', error);
    }
  }, []);

  function readIsMonitoring() {
    try {
      const script = `
      var isMonitoring = com.fanfanlo.emergencycall.manager.SensorManager.isMonitoring();
      isMonitoring;`;
      const { javaResultString } = autoWebViewJs.callScript(script);
      const isMonitoring = javaResultString === "true";
      // console.log("script get isMonitoring=", isMonitoring, javaResultString);
      setIsMonitoring(isMonitoring);
    } catch (error) {
      console.error('Error fetching initial monitoring state:', error);
    }
  }

  useEffect(() => {
    readIsMonitoring();
  }, []);

  const handleSetThreshold = () => {
    const thresholdValue = parseFloat(threshold);
    if (isNaN(thresholdValue)) {
      alert('请输入一个有效的数字作为阈值。');
      return;
    }
    try {
      const script = `com.fanfanlo.emergencycall.manager.SensorManager.updateThreshold(${thresholdValue});`;
      autoWebViewJs.callScript(script);
      alert(`阈值已设置为: ${thresholdValue}`);
    } catch (error) {
      console.error('Error calling updateThreshold:', error);
      alert('设置阈值失败。请确保您在安卓应用中。');
    }
  };

  async function handleToggleMonitoring () {
    if (isMonitoring) {
      // 如果当前正在监控，则直接停止，不进行检查
      try {
        const script = `com.fanfanlo.emergencycall.manager.SensorManager.stopMonitoring();`;
        autoWebViewJs.callScript(script);
        readIsMonitoring();
      } catch (error) {
        console.error('Error stopping monitoring:', error);
        alert('停止监控失败。');
      }
    } else {


      const appLoggedIn = await authService.showLoginDialog();
      if(!appLoggedIn){
        alert("未登录无法使用该功能")
        return
      }
      // 如果当前未监控，则检查权限，然后启动
      checkShowInfo(passed => {
        if (!passed) return; // 用户不同意，则不执行任何操作
        try {
          const script = `com.fanfanlo.emergencycall.manager.SensorManager.startMonitoring();`;
          // const script = `com.fanfanlo.emergencycall.manager.SensorManagerShell.startMonitoring();`;
          // const script = `com.fanfanlo.emergencycall.manager.SensorManagerShell.xx();`;
          autoWebViewJs.callScript(script);
          readIsMonitoring();
        } catch (error) {
          console.error('Error starting monitoring:', error);
          alert('开始监控失败。');
        }
      });
    }
  };

  useEffect(()=>{
    const id = setInterval(() => {
      readIsMonitoring();
    }, 3000); // 每5秒检查一次监控状态
    return () => clearInterval(id);
  }, [])
  function checkShowSensorInfo(callback: (passed: boolean) => void) {
    interface Info {
      passed: boolean;
    }
    const info = autoJsStoreUtils.read<Info>('showSensorInfo', { passed: false });
    if (!info || !info.passed) {
      // 保存回调，并在Dialog操作后调用
      sensorCallbackRef.current = callback;
      // 打开传感器权限对话框
      setSensorInfoDialogOpen(true);
    } else {
      callback(true);
    }
  }

  function checkShowInfo(callback:(passed:boolean)=>void){
    checkShowSensorInfo(passed=>{
      if(!passed) {
        callback(false);
        return;
      };
      checkShowForegroundServiceInfo(callback);
    })
  }

  function checkShowForegroundServiceInfo(callback: (passed: boolean) => void) {
    interface Info {
      passed: boolean;
    }
    const info = autoJsStoreUtils.read<Info>('showForegroundServiceInfo', { passed: false });
    if (!info || !info.passed) {
      // 保存回调，并在Dialog操作后调用
      foregroundServiceCallbackRef.current = callback;
      // 打开前台服务对话框
      setForegroundServiceDialogOpen(true);
    } else {
      callback(true);
    }
  }

  // 传感器对话框的处理器
  const handleSensorDialog = (passed: boolean) => {
    autoJsStoreUtils.write('showSensorInfo', { passed });
    setSensorInfoDialogOpen(false);
    if (sensorCallbackRef.current) {
      sensorCallbackRef.current(passed);
    }
  };

  // 前台服务对话框的处理器
  const handleForegroundServiceDialog = (passed: boolean) => {
    autoJsStoreUtils.write('showForegroundServiceInfo', { passed });
    setForegroundServiceDialogOpen(false);
    if (foregroundServiceCallbackRef.current) {
      foregroundServiceCallbackRef.current(passed);
    }
  };


  return (
    <Box>
      {/* 传感器监控控制面板 */}
      <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
        <Typography variant="h6" gutterBottom>
          传感器监控控制
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
          <TextField
            label="震动阈值"
            variant="outlined"
            type="number"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            size="small"
          />
          <Button variant="contained" onClick={handleSetThreshold}>
            设置
          </Button>
        </Box>
        <Button
          variant="contained"
          color={isMonitoring ? 'error' : 'primary'}
          onClick={handleToggleMonitoring}
          fullWidth
        >
          {isMonitoring ? '停止监控' : '开始监控'}
        </Button>
      </Paper>

      {/* 权限状态面板 */}
      <Accordion >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="permission-panel-content"
          id="permission-panel-header"
        >
          <Typography variant="h6">权限状态检查</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <PermissionStatusPanel />
        </AccordionDetails>
      </Accordion>

      {/* 传感器权限说明对话框 */}
      <Dialog
        open={isSensorInfoDialogOpen}
        onClose={() => handleSensorDialog(false)}
        aria-labelledby="sensor-dialog-title"
        aria-describedby="sensor-dialog-description"
      >
        <DialogTitle id="sensor-dialog-title">
          {"传感器使用授权"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="sensor-dialog-description">
            我们需要获取您的身体传感器（如加速度计）权限，以实现跌倒检测功能。这对于在紧急情况下自动触发呼叫至关重要。您是否同意？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleSensorDialog(false)}>不同意</Button>
          <Button onClick={() => handleSensorDialog(true)} autoFocus>
            同意
          </Button>
        </DialogActions>
      </Dialog>

      {/* 前台服务通知对话框 */}
      <Dialog
        open={isForegroundServiceDialogOpen}
        onClose={() => handleForegroundServiceDialog(false)}
        aria-labelledby="foreground-dialog-title"
        aria-describedby="foreground-dialog-description"
      >
        <DialogTitle id="foreground-dialog-title">
          {"启用前台服务通知"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="foreground-dialog-description">
            为了在应用退到后台后仍能持续监控传感器数据，我们需要启用一个前台服务。这会在您的设备通知栏显示一个持续的通知。您是否同意？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleForegroundServiceDialog(false)}>不同意</Button>
          <Button onClick={() => handleForegroundServiceDialog(true)} autoFocus>
            同意
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SensorMonitor;


